import { DateTime as _DateTime, Duration as _Duration, Interval as _Interval } from 'luxon';

export type DateTimeType = _DateTime;
export const DateTime = _DateTime;
export const Duration = _Duration;
export const Interval = _Interval;

export const DATE_TIME_FORMAT_24 = "dd/MM/yyyy 'at' HH:mm";
export const DATE_TIME_FORMAT = "dd/MM/yyyy 'at' h:mm a";

/**
 * Zoom-specific date utility that handles array formats from backend with timezone support.
 *
 * @param date - Date in string, Date, or array format
 * @param timezone - Optional IANA timezone (e.g., "Europe/London"). If omitted, converts to browser's local timezone.
 * @returns Luxon DateTime object in the specified timezone
 */
export const getZoomDateTime = (date: string | Date | number[], timezone?: string) => {
  if (typeof date === 'string') {
    const dt = DateTime.fromISO(date, { zone: 'utc' });
    return timezone ? dt.setZone(timezone) : dt.toLocal();
  } else if (date instanceof Date) {
    const dt = DateTime.fromJSDate(date, { zone: 'utc' });
    return timezone ? dt.setZone(timezone) : dt.toLocal();
  } else if (Array.isArray(date) && date.length >= 5) {
    // Handle array format [year, month, day, hour, minute] from Zoom backend
    const [year, month, day, hour, minute] = date;
    const dt = DateTime.fromObject(
      {
        year,
        month,
        day,
        hour,
        minute,
        second: 0,
        millisecond: 0
      },
      { zone: 'utc' }
    );
    return timezone ? dt.setZone(timezone) : dt.toLocal();
  } else if (Array.isArray(date) && date.length >= 3) {
    // Handle array format with just [year, month, day]
    const [year, month, day] = date;
    const dt = DateTime.fromObject(
      {
        year,
        month,
        day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      },
      { zone: 'utc' }
    );
    return timezone ? dt.setZone(timezone) : dt.toLocal();
  } else {
    // Return invalid DateTime for unsupported formats
    return DateTime.invalid('Unsupported date format');
  }
};

export const getZoomDateISOForServer = (date: string | Date | number[], timezone?: string) => {
  const datetime = getZoomDateTime(date, timezone);
  return datetime.isValid ? datetime.toUTC().toISO({ includeOffset: false, includePrefix: false }) : null;
};

/**
 * Formats date and time with timezone support.
 *
 * @param date - Date in string, Date, or array format
 * @param use24HourFormat - Whether to use 24-hour format (default: true)
 * @param timezone - Optional IANA timezone (e.g., "Europe/London"). If omitted, uses browser's local timezone.
 * @returns Formatted date-time string
 */
export function formatZoomDateAndTime(date: string | Date | number[], use24HourFormat = true, timezone?: string) {
  const datetime = getZoomDateTime(date, timezone);
  if (!datetime.isValid) {
    return '--/--/---- at --:--';
  }
  return datetime.toFormat(use24HourFormat ? DATE_TIME_FORMAT_24 : DATE_TIME_FORMAT);
}

/**
 * Formats date with timezone support.
 *
 * @param date - Date in string, Date, or array format
 * @param timezone - Optional IANA timezone (e.g., "Europe/London"). If omitted, uses browser's local timezone.
 * @returns Formatted date string
 */
export function formatZoomDate(date: string | Date | number[], timezone?: string) {
  const datetime = getZoomDateTime(date, timezone);
  if (!datetime.isValid) {
    return '--/--/----';
  }
  return datetime.toLocaleString(DateTime.DATE_SHORT);
}

/**
 * Formats time with timezone support.
 *
 * @param time - Date in string, Date, or array format
 * @param timezone - Optional IANA timezone (e.g., "Europe/London"). If omitted, uses browser's local timezone.
 * @returns Formatted time string
 */
export function formatZoomTime(time: string | Date | number[], timezone?: string) {
  const datetime = getZoomDateTime(time, timezone);
  if (!datetime.isValid) {
    return '--:--';
  }
  return datetime.toLocaleString(DateTime.TIME_SIMPLE);
}

export function isZoomDateTime(date: string) {
  const datetime = DateTime.fromISO(date);
  return datetime.hour !== 0 || datetime.minute !== 0 || datetime.second !== 0;
}

/**
 * Calculates the difference between two dates with timezone support.
 *
 * @param minuend - The date to subtract from
 * @param subtrahend - The date to subtract
 * @param timezone - Optional IANA timezone for both dates. If omitted, uses browser's local timezone.
 * @returns Luxon Duration representing the difference
 */
export function subtractZoomDates(minuend: string | Date | number[], subtrahend: string | Date | number[], timezone?: string) {
  return getZoomDateTime(minuend, timezone).diff(getZoomDateTime(subtrahend, timezone));
}

/**
 * Gets relative time format (e.g., "in 2 hours", "3 days ago") with timezone support.
 *
 * @param date - Date in string, Date, or array format
 * @param timezone - Optional IANA timezone (e.g., "Europe/London"). If omitted, uses browser's local timezone.
 * @returns Relative time string
 */
export const getZoomRelativeFormat = (date: string | Date | number[], timezone?: string) => {
  const datetime = getZoomDateTime(date, timezone);
  if (!datetime.isValid) {
    return '';
  }

  const units: Intl.RelativeTimeFormatUnit[] = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'];
  const diff = datetime.diffNow().shiftTo(...units);
  const unit = units.find((unit) => diff.get(unit) !== 0) || 'second';

  const relativeFormatter = new Intl.RelativeTimeFormat('en', {
    numeric: 'auto'
  });
  return relativeFormatter.format(Math.trunc(diff.as(unit)), unit);
};
